import classNames from "classnames";
import style from "./components.module.css";
import { ColumnPicker } from "./pickers/column-picker";
import { getPathPrefix } from "utils/getPathPrefix";

/**
 * Create a simple component that uses a single div with a className
 * @param className - The className to apply to the div
 * @returns A React component that renders a div with the given className
 */
function div(className: string): React.FC<React.PropsWithChildren<React.ComponentProps<"div">>> {
  //eslint-disable-next-line react/display-name, react/prop-types
  return ({ children, ...props }) => (
    <div className={className} {...props}>
      {children}
    </div>
  );
}

export const PopupBody = div(style.popupBody);
export const Stack = div(style.stack);
export const Section = div(classNames(style.section));
export const Box = div(style.box);
export const HR = div(style.hr);
export const HRLight = div(style.hrLight);
export const H1 = div(style.h1);
export const InputFieldsGrid = div(style.mappingInputArea);
export const Label = ({
  children,
  theme = "dark",
  ...props
}: React.PropsWithChildren<{ theme?: "light" | "dark" } & React.ComponentProps<"div">>) => (
  <div className={classNames(style.label, style[theme])} {...props}>
    {children}
  </div>
);

export const Arrow = () => (
  <svg width="16" height="9" viewBox="0 0 16 9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0.999741L15.5033 4.50195" stroke="#7F869B" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 8L15.5033 4.49779" stroke="#7F869B" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="0.5" y1="4.5" x2="14.5161" y2="4.5" stroke="#7F869B" strokeLinecap="round" />
  </svg>
);

export const ValidMarker = ({ children, invalid }: React.PropsWithChildren<{ invalid?: boolean }>) => (
  <span className={invalid ? style.invalidBorder : style.validBorder}>{children}</span>
);

export const PrimaryButton = ({ children, ...props }: React.PropsWithChildren<React.ComponentProps<"button">>) => (
  <button className={style.primaryButton} {...props}>
    {children}
  </button>
);

export const PsuedoDisabledButton = ({
  children,
  ...props
}: React.PropsWithChildren<React.ComponentProps<"button">>) => (
  <button className={style.psuedoDisabledButton} {...props}>
    {children}
  </button>
);

export const H1WithMondayLogo = ({ children }: React.PropsWithChildren<object>) => (
  <H1>
    <img
      draggable={false}
      style={{
        paddingInline: "0.5em",
      }}
      height={10}
      src={getPathPrefix("/images/monday-mini-logo.svg")}
      alt="Monday logo"
    />
    {children}
  </H1>
);

export const FieldMapping = ({
  label,
  value = "",
  onChange,
  options,
  placeholder = "Select column",
  testId,
  theme = "light",
}: {
  label: string;
  value?: string;
  onChange: (id: string) => void;
  options?: {
    id: string;
    title: string;
  }[];
  placeholder?: string;
  testId: string;
  theme?: "light" | "dark";
}) => (
  <>
    <Label theme={theme}>{label}</Label>
    <Arrow />
    <ColumnPicker
      testId={testId}
      selected={value}
      setSelected={onChange}
      options={options}
      placeholder={placeholder}
      theme={theme}
    />
  </>
);

export const ExpandableContent = ({ open, children }: React.PropsWithChildren<{ open: boolean }>) => {
  return (
    <div className={classNames(style.expandable, open && style.expanded)} tabIndex={-1}>
      <div className={style.expandableContent} tabIndex={-1}>
        {children}
      </div>
    </div>
  );
};

export const WarningIcon = (props: React.ComponentProps<"svg">) => (
  <svg width={13} height={13} viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path stroke="#F8746B" strokeLinecap="round" d="M6.551 8.75v-.1m0 .1v-.1m0-2.099v-2.3m0 2.3v-2.3" />
    <path
      d="M12 6.5a5.5 5.5 0 1 0-11 0 5.5 5.5 0 0 0 11 0"
      stroke="#F8746B"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
